import { useRef, useCallback, useState, useContext, useEffect } from "react"
import styles from "./styles.module.scss"
import { Wrapper, Page, Fade } from "components"
import { useMedia } from "components/hooks"
import { Item } from "./Item"
import { Indicator } from "./Indicator"
import type { FC, HTMLAttributes } from "react"

interface SectionProps
extends HTMLAttributes<HTMLDivElement> {}

function calculateNewIndex(index: number, limit: number, modifier: number) {
  return (!index && modifier < 0) 
      ? limit : (index == limit && modifier > 0)
      ? 0 : index + modifier
}

export const Section: FC<SectionProps> = ({ children, ...props }) => {
  const { content } = useContext(Page.Context)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isFocus, setFocusState] = useState(false)
  const isTouchScreen = useMedia({ other: ['hover: none', 'pointer: coarse']})
  const wrapperRef = useRef<HTMLUListElement>(null)
  const scrollHandler = useCallback(() => {
    const { scrollLeft, clientWidth } = wrapperRef.current
    const index = Math.round(scrollLeft / clientWidth)
    if (index !== activeIndex) setActiveIndex(index)
  }, [activeIndex, setActiveIndex])
  const clickHandler = useCallback((modifier: number) => () => {
    const { clientWidth } = wrapperRef.current
    const newActiveIndex = calculateNewIndex(
      activeIndex, 
      content.feedbackList.length - 1,
      modifier
    )
    setActiveIndex(newActiveIndex)
    wrapperRef.current.scrollTo({ left: clientWidth * newActiveIndex, behavior: 'smooth' })
  }, [activeIndex, setActiveIndex, content.feedbackList.length])
  const intervalHandler = useCallback(() => {
    if (isTouchScreen || isFocus) return
    clickHandler(+1)()
  }, [isFocus, isTouchScreen, clickHandler])

  useEffect(() => {
    const interval = setInterval(intervalHandler, 6000)
    return () => clearInterval(interval)
  }, [intervalHandler])

  return (
    <Page.Section.Container id="feedback" {...props}>
      <Fade>
        <Wrapper double
          onMouseEnter={setFocusState.bind(0, true)}
          onMouseLeave={setFocusState.bind(0, false)}>
          <Page.Section.Heading size="small" className={styles.heading}>
            {content.feedbackHeading}
          </Page.Section.Heading>
          <div className={styles.slider}>
            <div className={styles.controls}>
              <button onClick={clickHandler(-1)}>
                ← PREV
              </button>
              <button onClick={clickHandler(+1)}>
                NEXT →
              </button>
            </div>
            <ul className={styles.wrapper} ref={wrapperRef} onScroll={scrollHandler}>
              {content.feedbackList.map((feedbackItem, index) => (
                <Item key={index} active={activeIndex === index} {...feedbackItem} />
              ))}
            </ul>
            <div className={styles.indication}>
              {content.feedbackList.map((_, index) => (
                <Indicator key={index} active={activeIndex === index} />
              ))}
            </div>
          </div>
        </Wrapper> 
      </Fade>
    </Page.Section.Container>
  )
}
